import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { toast } from 'sonner';
import { ChevronUp, Inbox, LoaderCircle, MessageSquare, RefreshCw } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ConnectionDot } from '@/components/layout/AppHeader';
import { EmptyState } from '@/components/layout/EmptyState';
import { ConfusionMeter, PACE_TTL_MS, type PaceDirection } from '@/components/ConfusionMeter';
import { cn } from '@/lib/utils';
import { getStudentToken } from '../authCookie';

interface Question {
  question_id: string;
  question_text: string;
  votes: number;
  timestamp: string;
  is_answered: boolean;
}

interface StudentInterfaceProps {
  sessionId: string;
  classCode: string;
  className: string;
  backendUrl?: string;
  studentId?: string;
  sessionTimecreated?: string;
}

const DEFAULT_MAX_CHARS = 250;

function formatSessionDate(isoOrTimestamp: string): string {
  try {
    const d = new Date(isoOrTimestamp);
    if (Number.isNaN(d.getTime())) return isoOrTimestamp;
    return d.toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  } catch {
    return isoOrTimestamp;
  }
}

type WsOutgoing =
  // The socket carries the session token in this opening message: a browser
  // cannot set headers on a WebSocket, and a query string would leak the token
  // into proxy logs.
  | { type: 'hello'; role: 'student'; token: string }
  | { type: 'ping' };

type SessionEndedPayload = {
  type: 'session_ended';
  reason?: string; // "manual" | "prof_disconnected_timeout" | "prof_timeout" | ...
  outstanding_count?: number;
  answered_count?: number;
};

const StudentInterface: React.FC<StudentInterfaceProps> = ({
  sessionId,
  classCode,
  className,
  backendUrl = 'http://localhost:8000',
  studentId,
  sessionTimecreated,
}) => {
  const [questions, setQuestions] = useState<Question[]>([]);
  const [questionText, setQuestionText] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [upvotedQuestions, setUpvotedQuestions] = useState<Set<string>>(new Set());
  const [sessionEnded, setSessionEnded] = useState(false);

  const [maxChars, setMaxChars] = useState<number>(DEFAULT_MAX_CHARS);

  const [sessionEndedReason, setSessionEndedReason] = useState<string | null>(null);


  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimerRef = useRef<number | null>(null);
  const isUnmountingRef = useRef<boolean>(false);
  const reconnectAttemptRef = useRef<number>(0);
  const sessionEndedRef = useRef(false);

  const RECONNECT_BASE_MS = 1000;
  const RECONNECT_MAX_MS = 8000;

  const showSubmitBanner = useCallback((type: 'success' | 'error', message: string) => {
    if (type === 'success') toast.success(message);
    else toast.error(message);
  }, []);

  const trimmed = useMemo(() => questionText.trim(), [questionText]);
  const charCount = questionText.length;
  const isTooLong = charCount > maxChars;
  const isEmpty = trimmed.length === 0;

  const closeWs = useCallback(() => {
    if (wsRef.current) {
      try {
        wsRef.current.close();
      } catch {}
      wsRef.current = null;
    }
  }, []);

  const clearReconnectTimer = useCallback(() => {
    if (reconnectTimerRef.current != null) {
      window.clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  }, []);

  const authHeaders = useCallback((): Record<string, string> => {
    const token = getStudentToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const fetchQuestions = useCallback(async () => {
    try {
      const response = await fetch(`${backendUrl}/api/questions/${sessionId}`, {
        headers: authHeaders(),
      });
      const data = await response.json();
      setQuestions(data.questions || []);
      if (Array.isArray(data.my_upvotes)) {
        setUpvotedQuestions(new Set(data.my_upvotes as string[]));
      }
      setError(null);
    } catch (err) {
      console.error('Error fetching questions:', err);
      setError('Failed to fetch questions');
    }
  }, [sessionId, backendUrl, authHeaders]);

  const safeWsSend = useCallback((msg: WsOutgoing) => {
    const ws = wsRef.current;
    if (!ws) return;
    if (ws.readyState !== WebSocket.OPEN) return;
    try {
      ws.send(JSON.stringify(msg));
    } catch {}
  }, []);

  const connectWsRef = useRef<() => void>(() => {});
  const authFailedRef = useRef(false);

  const scheduleReconnect = useCallback(() => {
    if (isUnmountingRef.current) return;
    if (sessionEndedRef.current) return;
    // A rejected token is rejected identically every time, so retrying it just
    // loops forever behind a spinner. Stop and let the user log in again.
    if (authFailedRef.current) return;
    if (reconnectTimerRef.current != null) return;

    reconnectAttemptRef.current += 1;
    const backoff = Math.min(
      RECONNECT_MAX_MS,
      RECONNECT_BASE_MS * Math.pow(2, reconnectAttemptRef.current - 1)
    );
    const jitter = Math.floor(Math.random() * 300);
    const delay = backoff + jitter;

    reconnectTimerRef.current = window.setTimeout(() => {
      reconnectTimerRef.current = null;
      connectWsRef.current();
    }, delay);
  }, []);

  const handleSessionEnded = useCallback((payload?: SessionEndedPayload) => {
    setSessionEnded(true);
    sessionEndedRef.current = true;
    setQuestions([]);
    setIsConnected(false);
    setSessionEndedReason(payload?.reason || null);

    clearReconnectTimer();
    closeWs();
  }, [clearReconnectTimer, closeWs]);

  const [confusionLevel, setConfusionLevel] = useState<number | null>(null);
  const [pace, setPace] = useState<PaceDirection>(null);
  const paceTimerRef = useRef<number | null>(null);

  // The server drops a pace request once it goes stale; mirror that here so the
  // button never stays lit past the point where it still counts for anything.
  const armPaceExpiry = useCallback((direction: PaceDirection) => {
    if (paceTimerRef.current != null) window.clearTimeout(paceTimerRef.current);
    paceTimerRef.current = null;
    if (!direction) return;
    paceTimerRef.current = window.setTimeout(() => {
      paceTimerRef.current = null;
      setPace(null);
    }, PACE_TTL_MS);
  }, []);

  useEffect(() => {
    return () => {
      if (paceTimerRef.current != null) window.clearTimeout(paceTimerRef.current);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setConfusionLevel(null);
    setPace(null);
    armPaceExpiry(null);
    fetch(`${backendUrl}/api/confusion/${sessionId}`, { headers: authHeaders() })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (cancelled || !data) return;
        if (typeof data.my_level === 'number') setConfusionLevel(data.my_level);
        if (data.my_pace === 'faster' || data.my_pace === 'slower') {
          setPace(data.my_pace);
          // Only fresh requests come back, but not how much of their life is
          // left, so re-arm for a full window rather than expiring too early.
          armPaceExpiry(data.my_pace);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [sessionId, backendUrl, authHeaders, armPaceExpiry]);

  const updateConfusion = useCallback(
    async (level: number) => {
      setConfusionLevel(level);
      try {
        const res = await fetch(`${backendUrl}/api/confusion`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeaders() },
          body: JSON.stringify({ session_id: sessionId, level }),
        });
        if (res.status === 403) handleSessionEnded({ type: 'session_ended', reason: 'ended' });
      } catch (err) {
        console.error('Error updating confusion level:', err);
      }
    },
    [backendUrl, sessionId, authHeaders, handleSessionEnded]
  );

  const updatePace = useCallback(
    async (direction: PaceDirection) => {
      setPace(direction);
      armPaceExpiry(direction);
      try {
        const res = await fetch(`${backendUrl}/api/pace`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeaders() },
          body: JSON.stringify({ session_id: sessionId, direction }),
        });
        if (res.status === 403) handleSessionEnded({ type: 'session_ended', reason: 'ended' });
      } catch (err) {
        console.error('Error updating pace request:', err);
      }
    },
    [backendUrl, sessionId, authHeaders, handleSessionEnded, armPaceExpiry]
  );

  const connectWs = useCallback(() => {
    if (isUnmountingRef.current) return;
    if (sessionEndedRef.current) return;

    clearReconnectTimer();

    // Close any previous socket first.
    if (wsRef.current) {
      try {
        wsRef.current.close();
      } catch {}
      wsRef.current = null;
    }

    const wsProtocol = backendUrl.startsWith('https') ? 'wss' : 'ws';
    const wsUrl = `${wsProtocol}://${backendUrl.replace(/^https?:\/\//, '')}/ws/${sessionId}`;
    const websocket = new WebSocket(wsUrl);
    wsRef.current = websocket;

    websocket.onopen = () => {
      reconnectAttemptRef.current = 0;
      setIsConnected(true);
      setError(null);
      const token = getStudentToken();
      if (!token) {
        authFailedRef.current = true;
        setError('Your session expired. Please log in again.');
        return;
      }
      safeWsSend({ type: 'hello', role: 'student', token });
    };

    websocket.onmessage = (event) => {
      let message: any;
      try {
        message = JSON.parse(event.data);
      } catch {
        return;
      }

      if (message.type === 'new_question') {
        setQuestions((prev) => [...prev, message.question]);
        return;
      }

      if (message.type === 'question_answered') {
        setQuestions((prev) =>
          prev.map((q) => (q.question_id === message.question_id ? { ...q, is_answered: true } : q))
        );
        return;
      }

      if (message.type === 'question_upvoted') {
        setQuestions((prev) =>
          prev.map((q) => (q.question_id === message.question_id ? { ...q, votes: message.votes } : q))
        );
        if (studentId && message.student_id === studentId) {
          setUpvotedQuestions((prev) => new Set(Array.from(prev).concat(String(message.question_id))));
        }
        return;
      }

      if (message.type === 'question_downvoted') {
        setQuestions((prev) =>
          prev.map((q) => (q.question_id === message.question_id ? { ...q, votes: message.votes } : q))
        );
        if (studentId && message.student_id === studentId) {
          setUpvotedQuestions((prev) => {
            const ns = new Set(prev);
            ns.delete(String(message.question_id));
            return ns;
          });
        }
        return;
      }

      if (message.type === 'session_ended') {
        handleSessionEnded(message as SessionEndedPayload);
        return;
      }

      if (message.type === 'error') {
        if (message.code === 'auth_required') {
          authFailedRef.current = true;
          setError('Your session expired. Please log in again.');
          return;
        }
        // Backend might send errors like "Session not found"
        setError(message.message || 'Server error');
      }
    };

    websocket.onerror = (errorEvent) => {
      console.error('WebSocket error:', errorEvent);
      setError('WebSocket connection error');
      setIsConnected(false);
    };

    websocket.onclose = () => {
      setIsConnected(false);
      if (!isUnmountingRef.current && !sessionEndedRef.current) {
        scheduleReconnect();
      }
    };
  }, [
    backendUrl,
    sessionId,
    safeWsSend,
    scheduleReconnect,
    clearReconnectTimer,
    handleSessionEnded,
    studentId
  ]);

  connectWsRef.current = connectWs;

  useEffect(() => {
    setSessionEnded(false);
    sessionEndedRef.current = false;
    setSessionEndedReason(null);
    setError(null);
    setUpvotedQuestions(new Set());
    setQuestionText('');

    fetchQuestions();

    isUnmountingRef.current = false;

    const connectTimer = window.setTimeout(() => {
      connectWsRef.current();
    }, 0);

    return () => {
      isUnmountingRef.current = true;
      window.clearTimeout(connectTimer);
      clearReconnectTimer();
      closeWs();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, backendUrl, fetchQuestions, clearReconnectTimer, closeWs]);

  const handleSubmitQuestion = async (e: React.FormEvent) => {
    e.preventDefault();

    if (sessionEnded) {
      setError('This session has ended. You can’t submit new questions.');
      return;
    }
    if (isEmpty) {
      setError('Please enter a question');
      return;
    }
    if (isTooLong) {
      setError(`Your question is too long. Please keep it under ${maxChars} characters.`);
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch(`${backendUrl}/api/questions/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
          session_id: sessionId,
          question_text: questionText,
        }),
      });

      if (!response.ok) {
        let message = 'Failed to submit question';

        try {
          const data = await response.json();
          const detail = data?.detail;

          if (detail && typeof detail === 'object') {
            if (typeof detail.max_chars === 'number' && detail.max_chars > 0) {
              setMaxChars(detail.max_chars);
            }

            if (typeof detail.reason === 'string' && detail.reason.trim()) {
              message = detail.reason;
            } else if (typeof detail.message === 'string' && detail.message.trim()) {
              message = detail.message;
            } else {
              message = 'There was a problem with your request.';
            }

            const labels: unknown = detail.labels;
            if (Array.isArray(labels) && labels.includes('too_long') && typeof detail.max_chars === 'number') {
              message = `Your question is too long. Max ${detail.max_chars} characters.`;
            }
          } else if (response.status === 429) {
            message = 'You are submitting questions too quickly. Please wait a bit and try again.';
          } else if (detail) {
            message = typeof detail === 'string' ? detail : 'There was a problem with your request.';
          }

          if (response.status === 403) {
            // backend says session ended
            handleSessionEnded({ type: 'session_ended', reason: 'ended' });
          }
        } catch {}

        setError(message);
        showSubmitBanner('error', message);
        return;
      }

      setQuestionText('');
      showSubmitBanner('success', 'Question submitted!');
    } catch (err) {
      console.error('Error submitting question:', err);
      const message = 'Failed to submit question';
      setError(message);
      showSubmitBanner('error', message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpvote = async (questionId: string) => {
    if (sessionEnded) {
      setError('This session has ended. Voting is disabled.');
      return;
    }

    const isAlreadyUpvoted = upvotedQuestions.has(questionId);
    const endpoint = isAlreadyUpvoted ? 'downvote' : 'upvote';

    setError(null);

    try {
      const response = await fetch(`${backendUrl}/api/questions/${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
          session_id: sessionId,
          question_id: questionId,
        }),
      });

      if (!response.ok) {
        let message = `Failed to ${endpoint} question`;

        try {
          const data = await response.json();
          const detail = data?.detail;

          if (response.status === 429) {
            message = 'You are voting too quickly. Please slow down and try again.';
          } else if (detail) {
            message = typeof detail === 'string' ? detail : 'There was a problem with your request.';
          }

          if (response.status === 403) {
            handleSessionEnded({ type: 'session_ended', reason: 'ended' });
          }
        } catch {}

        setError(message);
        return;
      }

      if (isAlreadyUpvoted) {
        setUpvotedQuestions((prev) => {
          const ns = new Set(prev);
          ns.delete(questionId);
          return ns;
        });
      } else {
        setUpvotedQuestions((prev) => new Set(Array.from(prev).concat(String(questionId))));
      }
    } catch (err) {
      console.error(`Error ${endpoint}ing question:`, err);
      setError(`Failed to ${endpoint} question`);
    }
  };

  const sortedQuestions = [...questions]
    .filter((q) => !q.is_answered)
    .sort((a, b) => {
      if (b.votes !== a.votes) return b.votes - a.votes;
      return a.timestamp.localeCompare(b.timestamp);
    });

  if (sessionEnded) {
    const reason = sessionEndedReason || '';
    const humanReason =
      reason === 'manual'
        ? 'Your professor ended the session.'
        : reason === 'prof_disconnected_timeout' || reason === 'prof_timeout'
        ? 'Your professor is no longer connected.'
        : 'This session is no longer active.';

    return (
      <main className="container max-w-md py-16">
        <div className="rounded-2xl border bg-card shadow-soft">
          <EmptyState
            icon={<MessageSquare />}
            title="Class has ended"
            description={`${humanReason} If another session starts, the same link or code will get you back in.`}
          />
          <div className="border-t p-4">
            <Button variant="outline" className="w-full" onClick={() => window.location.reload()}>
              <RefreshCw aria-hidden />
              Check again
            </Button>
          </div>
        </div>
      </main>
    );
  }

  const submitDisabled = isSubmitting || isEmpty || isTooLong;

  return (
    <main className="container max-w-3xl py-10">
      <div className="min-w-0">
        <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">{className}</h1>
        <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
          <span className="font-medium text-foreground">{classCode}</span>
          {sessionTimecreated && <span>Began {formatSessionDate(sessionTimecreated)}</span>}
          <ConnectionDot connected={isConnected} />
        </div>
      </div>

      <ConfusionMeter
        value={confusionLevel}
        onChange={updateConfusion}
        pace={pace}
        onPaceChange={updatePace}
        className="mt-8"
      />

      {error && (
        <Alert variant="destructive" className="mt-6 whitespace-pre-wrap">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <form
        onSubmit={handleSubmitQuestion}
        className={cn(
          'mt-8 rounded-2xl border bg-card shadow-soft transition-shadow focus-within:shadow-card',
          isTooLong && 'border-destructive'
        )}
      >
        <Textarea
          value={questionText}
          onChange={(e) => {
            setQuestionText(e.target.value);
            if (error && e.target.value.length <= maxChars) setError(null);
          }}
          placeholder="Ask a question..."
          rows={3}
          className="min-h-[112px] resize-none rounded-2xl rounded-b-none border-0 px-5 pt-5 text-base shadow-none focus-visible:ring-0 md:text-base"
        />
        <div className="flex flex-wrap items-center justify-between gap-3 px-5 pb-4 pt-1">
          <span
            className={cn(
              'text-xs tabular-nums',
              isTooLong ? 'font-medium text-destructive-soft-foreground' : 'text-muted-foreground'
            )}
          >
            {isTooLong ? `${charCount - maxChars} over the ${maxChars} character limit` : `${charCount}/${maxChars}`}
          </span>
          <div className="flex items-center gap-3">
            {isSubmitting && (
              <span className="hidden items-center gap-1.5 text-xs text-muted-foreground sm:flex">
                <LoaderCircle className="h-3.5 w-3.5 animate-spin" aria-hidden />
                Checking relevance
              </span>
            )}
            <Button
              type="submit"
              variant="brand"
              className="rounded-full"
              disabled={submitDisabled}
              title={
                isEmpty
                  ? 'Enter a question to submit'
                  : isTooLong
                  ? `Question must be ${maxChars} characters or fewer`
                  : undefined
              }
            >
              {isSubmitting ? 'Submitting...' : 'Ask'}
            </Button>
          </div>
        </div>
      </form>

      <section className="mt-10">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold tracking-tight">Questions</h2>
          <span className="text-sm text-muted-foreground">{sortedQuestions.length} open</span>
        </div>
        {sortedQuestions.length === 0 ? (
          <div className="mt-4 rounded-xl border border-dashed">
            <EmptyState
              icon={<Inbox />}
              title="No questions yet"
              description="Be the first to ask. Upvote others' questions to move them up the list."
            />
          </div>
        ) : (
          <ul className="mt-4 divide-y rounded-xl border bg-card">
            {sortedQuestions.map((question) => {
              const upvoted = upvotedQuestions.has(question.question_id);
              return (
                <li key={question.question_id} className="flex items-start gap-4 p-4">
                  <button
                    type="button"
                    onClick={() => handleUpvote(question.question_id)}
                    aria-pressed={upvoted}
                    title={upvoted ? 'Remove upvote' : 'Upvote'}
                    className={cn(
                      'flex h-9 shrink-0 items-center gap-1 rounded-full border px-3 text-sm font-semibold tabular-nums transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                      upvoted
                        ? 'border-foreground bg-foreground text-background hover:bg-primary-hover'
                        : 'border-border bg-background text-foreground hover:border-foreground'
                    )}
                  >
                    <ChevronUp className="h-4 w-4" aria-hidden />
                    {question.votes}
                  </button>
                  <p className="flex-1 pt-1.5 text-[15px] leading-relaxed">{question.question_text}</p>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </main>
  );
};

export default StudentInterface;
